const LS_KEY_HIDE_INSTALL = "tiempos_pwa_hide_install";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

type InstallAvailabilityListener = (available: boolean) => void;

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let listenersConfigured = false;
let swRegisterStarted = false;
let installPromptShownInSession = false;
const installAvailabilityListeners = new Set<InstallAvailabilityListener>();

function notifyInstallAvailability(available: boolean): void {
  for (const listener of installAvailabilityListeners) {
    listener(available);
  }
}

function canShowInstallPrompt(): boolean {
  return !localStorage.getItem(LS_KEY_HIDE_INSTALL) && !installPromptShownInSession;
}

function ensureInstallPromptListeners(): void {
  if (listenersConfigured) {
    return;
  }

  const onBeforeInstall = (event: Event) => {
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;

    if (canShowInstallPrompt()) {
      installPromptShownInSession = true;
      notifyInstallAvailability(true);
    }
  };

  const onInstalled = () => {
    deferredPrompt = null;
    notifyInstallAvailability(false);
  };

  window.addEventListener("beforeinstallprompt", onBeforeInstall);
  window.addEventListener("appinstalled", onInstalled);
  listenersConfigured = true;
}

export function setupPwaInstallPrompt(onAvailable?: (available: boolean) => void): () => void {
  ensureInstallPromptListeners();

  if (onAvailable) {
    installAvailabilityListeners.add(onAvailable);

    // Sincroniza inmediatamente el estado del modal para nuevos suscriptores.
    if (deferredPrompt && canShowInstallPrompt()) {
      onAvailable(true);
    }
  }

  return () => {
    if (onAvailable) {
      installAvailabilityListeners.delete(onAvailable);
    }
  };
}

export function isStandalonePwa(): boolean {
  return window.matchMedia("(display-mode: standalone)").matches || (window.navigator as any).standalone === true;
}

export async function triggerPwaInstall(): Promise<boolean> {
  if (!deferredPrompt) {
    return false;
  }

  await deferredPrompt.prompt();
  const result = await deferredPrompt.userChoice;
  deferredPrompt = null;

  if (result.outcome === "accepted") {
    notifyInstallAvailability(false);
  }

  return result.outcome === "accepted";
}

export function dismissPwaInstallPrompt(): void {
  localStorage.setItem(LS_KEY_HIDE_INSTALL, "1");
  notifyInstallAvailability(false);
}

export function registerServiceWorker(): void {
  if (!("serviceWorker" in navigator) || swRegisterStarted) {
    return;
  }

  swRegisterStarted = true;

  const register = async () => {
    try {
      const reg = await navigator.serviceWorker.register("/service-worker.js");

      // Detectar nuevo SW instalado: si el usuario ya tiene un SW controlando
      // la página, y llega uno nuevo, recargar automáticamente para evitar
      // que index.html viejo deje la pantalla en blanco.
      reg.addEventListener("updatefound", () => {
        const newSW = reg.installing;
        if (!newSW) return;
        newSW.addEventListener("statechange", () => {
          // Solo recargar si ya había un SW activo (no en primera instalación)
          if (newSW.state === "activated" && navigator.serviceWorker.controller) {
            window.location.reload();
          }
        });
      });
    } catch (err) {
      console.error("[PWA] Error registrando service worker", err);
      swRegisterStarted = false;
    }
  };

  // También recargar si el controlador cambia (otro tab activó nuevo SW)
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    window.location.reload();
  });

  if (document.readyState === "complete") {
    register();
    return;
  }

  window.addEventListener("load", () => register(), { once: true });
}
